import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLayoutNavigation } from '@hooks/useLayoutNavigation';
import { useCoreLayout } from '@contexts/layout';
import { useProfile } from '@contexts/profile/ProfileContext';
import { consume } from '@utils/returnTo';
import PreservoirLogomark from '@assets/brand/preservoir-logomark.svg';
import styles from './Welcome.module.css';

const Welcome = () => {
  const [fadeOut, setFadeOut] = useState(false);
  const [showLogo, setShowLogo] = useState(false);
  const [showText, setShowText] = useState(false);
  const [fadeElements, setFadeElements] = useState(false);
  const [navigationReady, setNavigationReady] = useState(false);
  const [nextRoute, setNextRoute] = useState<string | null>(null);
  const [showGradient, setShowGradient] = useState(false);
  const [fadeOutGradient, setFadeOutGradient] = useState(false);
  const { profileData, availableBusinesses, switchBusiness } = useProfile();
  const navigate = useNavigate();
  const { navigateToLogoOnlyRoute, navigateWithAnimation } = useLayoutNavigation();
  const { hideGradient: hideLayoutGradient } = useCoreLayout();

  // Check businesses as soon as they're available and prepare navigation
  useEffect(() => {
    if (availableBusinesses.length > 0) {
      // Determine next route based on business count
      if (availableBusinesses.length > 1) {
        setNextRoute('/business-select');
      } else {
        // Auto-select single business and go directly to dashboard
        switchBusiness(availableBusinesses[0].id);
        setNextRoute('/dashboard');
      }
      setNavigationReady(true);
    } else if (profileData && availableBusinesses.length === 0) {
      // No businesses available - go to the business-select screen
      // (handles the empty-list case); there is no separate pending screen.
      setNextRoute('/business-select');
      setNavigationReady(true);
    }
  }, [availableBusinesses, profileData, switchBusiness]);

  useEffect(() => {
    // Mark welcome as shown in this session
    sessionStorage.setItem('welcome-shown', 'true');

    // IMPORTANT: Ensure layout gradient is hidden immediately
    hideLayoutGradient();

    // Start gradient animation immediately
    setShowGradient(true);

    // Logo and text animations
    const logoTimer = setTimeout(() => {
      setShowLogo(true);
    }, 200);

    const textTimer = setTimeout(() => {
      setShowText(true);
    }, 600);

    // Fade out elements after 3 seconds
    const fadeElementsTimer = setTimeout(() => {
      setFadeElements(true);
    }, 1800);

    // Transition after fade out completes - navigate with proper layout animations
    const transitionTimer = setTimeout(() => {
      // Start gradient fade out animation
      setFadeOutGradient(true);

      setTimeout(() => {
        setFadeOut(true);

        setTimeout(() => {
          if (navigationReady && nextRoute) {
            if (nextRoute === '/business-select') {
              // Navigate to business-select (logo-only route) with smooth transition
              navigateToLogoOnlyRoute(nextRoute, {
                fadeToColor: 'var(--surface-auth)',
                showGradient: false,
              });
            } else if (nextRoute === '/dashboard') {
              // Navigate to dashboard (full layout route) with smooth animation and gradient fade in
              navigateWithAnimation(consume(nextRoute), { showGradient: true });
            } else {
              // Fallback to immediate navigation for unknown routes
              navigate(nextRoute);
            }
          } else {
            // Fallback to dashboard if navigation not ready
            navigateWithAnimation('/dashboard', { showGradient: true });
          }
        }, 100);
      }, 600); // Wait for gradient fade
    }, 1800);

    return () => {
      clearTimeout(logoTimer);
      clearTimeout(textTimer);
      clearTimeout(fadeElementsTimer);
      clearTimeout(transitionTimer);
    };
  }, [
    navigationReady,
    nextRoute,
    navigate,
    navigateToLogoOnlyRoute,
    navigateWithAnimation,
    hideLayoutGradient,
  ]);

  return (
    <div className={`${styles.welcomeScreen} ${fadeOut ? styles.fadeOut : ''}`}>
      <div
        className={`${styles.welcomeGradient} ${showGradient ? styles.fadeIn : ''} ${fadeOutGradient ? styles.fadeOut : ''}`}
      ></div>
      <div className={`${styles.welcomeContent} ${fadeElements ? styles.fadeOut : ''}`}>
        <div
          className={`${styles.welcomeLogo} ${showLogo ? styles.fadeIn : ''} ${showText ? styles.moveUp : ''}`}
        >
          <img src={PreservoirLogomark} alt="Preservoir" />
        </div>
        <div className={`${styles.welcomeMessage} ${showText ? styles.fadeIn : ''}`}>
          <h1>Welcome back, {profileData?.firstName}.</h1>
        </div>
      </div>
    </div>
  );
};

export default Welcome;
